import { useDrawer } from '@billy/design-system'
import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { isTransactionsPagePath } from '../utils/isTransactionsPagePath'

const TRANSACTION_ID_PARAM = 'transactionId'

// Called only while TransactionDrawerContent is mounted (i.e. drawer is open).
// Adds the param on mount, removes it on unmount or when transactionId changes.
// We don't want this kind of use on other pages, so it is explicitly gated to only work on the transactions page
export const useTransactionDrawerUrlSync = (transactionId?: string): void => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEnabled = isTransactionsPagePath(location.pathname)
  /* Read via ref (not a `location` dependency) so mount/cleanup always sees the current search string
     instead of the one captured when this effect was created, and so calling navigate() below doesn't retrigger it */
  const locationRef = useRef(location)
  locationRef.current = location

  const { isOpen } = useDrawer()

  useEffect(() => {
    if (!transactionId || !isEnabled || !isOpen) {
      return
    }

    const params = new URLSearchParams(locationRef.current.search)
    params.set(TRANSACTION_ID_PARAM, transactionId)
    navigate({ search: params.toString() }, { replace: true })

    /* cleanup on unmount or when transactionId changes - should not happen */
    return () => {
      const cleanupParams = new URLSearchParams(locationRef.current.search)

      if (cleanupParams.get(TRANSACTION_ID_PARAM) === transactionId) {
        cleanupParams.delete(TRANSACTION_ID_PARAM)
        navigate({ search: cleanupParams.toString() }, { replace: true })
      }
    }
  }, [transactionId, navigate, isEnabled, isOpen])
}
